<template>
    <v-app id="app">
        <GlobalNavigationProgress />
        <component v-if="!hideAppChrome" :is="headerComponent"></component>
        <div style="display: flex; flex-direction: column">
            <div :class="['content-wrapper', { 'content-wrapper--fullbleed': hideAppChrome }]">
                <transition name="action-bar-morph" mode="out-in">
                    <!-- Save bar wins when a form has registered an action and
                         become dirty. Same visual slot as the dock so the
                         user perceives one element morphing, not two. -->
                    <div
                        v-if="showSaveBar && !hideAppDock"
                        key="save"
                        class="action-bar action-bar--save"
                    >
                        <button
                            type="button"
                            class="action-bar__cancel"
                            :disabled="saveBarState.saving"
                            @click="onCancelClick"
                        >
                            <v-icon size="18">mdi-close</v-icon>
                            <span>{{ saveBarState.cancelLabel }}</span>
                        </button>
                        <button
                            type="button"
                            class="action-bar__save"
                            :disabled="saveBarState.saving"
                            @click="onSaveClick"
                        >
                            <v-icon size="18">{{
                                saveBarState.saving
                                    ? 'mdi-loading mdi-spin'
                                    : 'mdi-content-save-check-outline'
                            }}</v-icon>
                            <span>{{
                                saveBarState.saving ? 'Сохраняем…' : saveBarState.saveLabel
                            }}</span>
                        </button>
                        <p v-if="saveBarState.error" class="action-bar__error" role="alert">
                            {{ saveBarState.error }}
                        </p>
                    </div>

                    <!-- Default state: floating app-dock with primary nav. -->
                    <div v-else-if="!hideAppDock" key="dock" class="app-dock">
                        <nav class="nav-menu">
                            <button
                                v-for="item in navItems"
                                :key="item.id"
                                :class="[
                                    'nav-item',
                                    {
                                        active: activeNavItem === item.id,
                                        'nav-item--alert':
                                            item.id === 'calendar' && calendarHasPendingHold,
                                    },
                                    `nav-item--${item.id}`,
                                ]"
                                @click="handleNavItemClick(item.id)"
                            >
                                <div class="icon-container">
                                    <span class="nav-icon-slot" v-html="iconItems[item.id]"></span>
                                    <span
                                        v-if="item.id === 'calendar' && calendarHasPendingHold"
                                        class="nav-badge nav-badge--mobile nav-badge--alert"
                                    >
                                        {{ calendarPendingHoldCount }}
                                    </span>
                                    <span
                                        v-else-if="item.id === 'calendar' && calendarUpcomingCount"
                                        class="nav-badge nav-badge--mobile nav-badge--upcoming"
                                    >
                                        {{ calendarUpcomingCount }}
                                    </span>
                                    <span
                                        v-if="item.id === 'calendar' && calendarHasNew"
                                        class="nav-dot nav-dot--new"
                                        aria-hidden="true"
                                    ></span>
                                </div>
                                <span class="nav-text">{{ item.text }}</span>
                            </button>
                        </nav>
                    </div>
                </transition>

                <main
                    :class="[
                        'main-content',
                        {
                            'main-content--fullbleed': hideAppChrome,
                            'main-content--dockless': hideAppDock,
                        },
                    ]"
                >
                    <router-view />
                </main>
                <component
                    v-if="!hideAppChrome"
                    :is="footerComponent"
                    :is-admin="isAdmin"
                ></component>
            </div>
        </div>
        <AnalyticsConsentBanner />
        <GlobalToastHost />
        <GlobalSupportFab />
    </v-app>
</template>

<script lang="ts">
/* eslint-disable */
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import store from '../store';
import { subscribe } from '@/shared/lib/invalidationBus';

import HeaderDefault from './components/Layouts/HeaderDefault.vue';
import FooterComponentVue from './components/Layouts/FooterComponent.vue';
import FooterLandingComponent from './components/Layouts/FooterLandingComponent.vue';

import JumpingDotLoaderWithShadows from './components/Animations/JumpingDotLoaderWithShadows.vue';
import GlobalToastHost from './components/common/GlobalToastHost.vue';
import GlobalNavigationProgress from './components/common/GlobalNavigationProgress.vue';
import AnalyticsConsentBanner from './components/common/AnalyticsConsentBanner.vue';
import GlobalSupportFab from './components/support/GlobalSupportFab.vue';
import { mentorsCatalogEnabled as readMentorsCatalogEnabled } from '@/shared/platformFlags';
import { buildAppNavItems } from '@/shared/lib/appNavItems';
import { useIsAdmin } from '@/composables/useIsAdmin';

import { VApp } from 'vuetify/components';

export default {
    name: 'App',
    components: {
        VApp,
        FooterComponentVue,
        HeaderDefault,
        FooterLandingComponent,
        JumpingDotLoaderWithShadows,
        GlobalToastHost,
        GlobalNavigationProgress,
        AnalyticsConsentBanner,
        GlobalSupportFab,
    },
    setup() {
        const styleEl = document.createElement('style');

        onMounted(() => {
            document.head.appendChild(styleEl);
        });

        // ----------------------------
        // 1) Авторизация
        // ----------------------------
        const isAuthenticated = computed(() => store.getters.isAuthenticated);

        // ----------------------------
        // 2) Навигация (sidebar / footer)
        // ----------------------------
        const activeNavItem = ref<string | undefined>(undefined);
        const router = useRouter();
        const route = useRoute();

        // Фичефлаги — build-time env (см. @/shared/platformFlags). Менять
        // через env при деплое, не через runtime API.
        const mentorsCatalogEnabled = computed(() => readMentorsCatalogEnabled());
        // Роль нужна, чтобы у наставника прятать «Поиск» — свой каталог ему не
        // помогает: он сам поставщик услуг, а не покупатель.
        const userRole = computed(() => store.getters.userRole as string | null);

        // Каталог наставников (/AllMentors) временно доступен только админам —
        // и вкладка дока, и ссылка в футере гейтятся этим признаком (тот же
        // источник, что у route-guard'а: /admin-api/me/).
        const isAdmin = useIsAdmin();

        // Сборка вынесена в shared/lib/appNavItems.ts — чтобы тесты могли
        // фиксировать контракт (роль → видимые вкладки → корректные роуты)
        // без поднятия Vuetify/Router/Vuex стэка целиком.
        const navItems = computed(() =>
            buildAppNavItems({
                userRole: userRole.value,
                mentorsCatalogEnabled: mentorsCatalogEnabled.value,
                isAdmin: isAdmin.value,
            }),
        );

        const calendarSummary = computed(
            () => store.getters.calendarSummary || { upcoming: 0, pendingHolds: 0, overdue: 0 },
        );
        const calendarBadgeCount = computed(() => {
            const summary = calendarSummary.value || { upcoming: 0, pendingHolds: 0 };
            const total = (Number(summary.upcoming) || 0) + (Number(summary.pendingHolds) || 0);
            return total > 0 ? total : undefined;
        });
        // Подсветка календаря только при незавершённых оплатах (pending-холды)
        const calendarHasPendingHold = computed(() => {
            const summary = calendarSummary.value || { pendingHolds: 0 };
            return (Number(summary.pendingHolds) || 0) > 0;
        });

        const calendarUpcomingCount = computed(() => {
            return Number(calendarSummary.value?.upcoming) || 0;
        });

        // Число pending-холдов для «alert»-бейджа дока. Отдельный returned-computed
        // (а не `calendarSummary.pendingHolds` в шаблоне): calendarSummary в
        // setup-return не отдаётся, поэтому прямой доступ из шаблона падал при
        // рендере (undefined.pendingHolds) как только появлялся холд — бейдж
        // «замерзал» до перезагрузки (баг #5). Зеркалит calendarUpcomingCount.
        const calendarPendingHoldCount = computed(() => {
            return Number(calendarSummary.value?.pendingHolds) || 0;
        });

        // «Новое в календаре» — точка на вкладке Календарь до открытия календаря.
        const calendarHasNew = computed<boolean>(() => Boolean(store.getters.calendarHasNew));

        // Связь уведомления/локальные действия → календарь-док: на publish('calendar')
        // (из realtime-моста useNotificationsRealtime и из success-путей брони/записи)
        // обновляем сводку дока и поднимаем «новое».
        let offCalendarInvalidate: (() => void) | null = null;
        onMounted(() => {
            offCalendarInvalidate = subscribe('calendar', () => {
                store.dispatch('markCalendarChanged').catch(() => undefined);
            });
        });
        onUnmounted(() => {
            offCalendarInvalidate?.();
            offCalendarInvalidate = null;
        });

        const iconItems: Record<string, string> = {
            home: `
        <svg class="nav-icon nav-icon--home" width="26" height="26" viewBox="0 -5 24 29" fill="none" xmlns="http://www.w3.org/2000/svg">
          <!-- Chimney drawn first so the roof stroke overlaps and hides its bottom seam. -->
          <rect class="nav-home-chimney" x="15.6" y="2.5" width="1.8" height="3.6" fill="currentColor"/>
          <path class="nav-home-roof" pathLength="1" d="M3 10.75L12 3l9 7.75" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="nav-home-body" d="M5 11V20a1.25 1.25 0 0 0 1.25 1.25H10V14.5h4v6.75h3.75A1.25 1.25 0 0 0 19 20V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <rect class="nav-home-door" x="10" y="14.5" width="4" height="6.75" rx="0.4" fill="currentColor"/>
          <!-- Doodle smoke: a continuous curve from the chimney whose control
               points cross over each other — produces the self-intersecting,
               kid-drawing look the user asked for. pathLength=1 lets the
               keyframes draw it in via stroke-dashoffset 1 → 0. -->
          <path class="nav-home-smoke" pathLength="1"
                d="M 16.5,2.3 c 2,-1 -3,-2 -1,-2.5 c -2,-1 3,-2 1,-2.5"
                stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
        </svg>
      `,
            analytics: `
        <svg class="nav-icon nav-icon--analytics" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <line class="nav-analytics-axis" x1="2" y1="20" x2="20" y2="20" stroke="currentColor" stroke-width="2.04545" stroke-linecap="round"/>
          <polyline class="nav-analytics-line" pathLength="1" points="2,16 7,10 12,13 20,4" stroke="currentColor" stroke-width="2.04545" stroke-linecap="round" stroke-linejoin="round"/>
          <polyline class="nav-analytics-arrow" points="16.4,4 20,4 20,7.6" stroke="currentColor" stroke-width="2.04545" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
    `,
            calendar: `
        <svg class="nav-icon nav-icon--calendar" width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect class="nav-calendar-day" x="13.1" y="15" width="4.8" height="4.8" rx="1.2" fill="currentColor"/>
          <path class="nav-calendar-rings" d="M8.11111 6.88889V2M17.8889 6.88889V2" stroke="currentColor" stroke-width="2.04545" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="nav-calendar-divider" d="M6.88889 11.7778H19.1111" stroke="currentColor" stroke-width="2.04545" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="nav-calendar-frame" d="M4.44444 24H21.5556C22.9056 24 24 22.9056 24 21.5556V6.88889C24 5.53886 22.9056 4.44444 21.5556 4.44444H4.44444C3.09442 4.44444 2 5.53886 2 6.88889V21.5556C2 22.9056 3.09441 24 4.44444 24Z" stroke="currentColor" stroke-width="2.04545" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `,
            chats: `
        <svg width="26" height="20" viewBox="0 0 26 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 5.11149L11.6441 11.5409C12.4652 12.0883 13.5348 12.0883 14.3559 11.5409L24 5.11149M4.44444 18.5559H21.5556C22.9056 18.5559 24 17.4615 24 16.1115V3.88927C24 2.53924 22.9056 1.44482 21.5556 1.44482H4.44444C3.09442 1.44482 2 2.53924 2 3.88927V16.1115C2 17.4615 3.09441 18.5559 4.44444 18.5559Z" stroke="currentColor" stroke-width="2.04545" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `,
            create: `
        <svg class="nav-icon nav-icon--create" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g class="nav-create-plus">
            <path class="nav-create-vertical" pathLength="1" d="M11.0004 1.22217V20.7777" stroke="currentColor" stroke-width="2.04545" stroke-linecap="round"/>
            <path class="nav-create-horizontal" pathLength="1" d="M20.7782 10.9999L1.22266 10.9999" stroke="currentColor" stroke-width="2.04545" stroke-linecap="round"/>
          </g>
        </svg>
      `,
            menteeLanding: `
        <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.428 12.4282C18.1488 12.149 17.7932 11.9587 17.406 11.8812L15.0185 11.4037C13.7101 11.1421 12.3519 11.324 11.1585 11.9207L10.8411 12.0793C9.64772 12.676 8.28948 12.8579 6.98113 12.5963L5.04938 12.2099C4.39366 12.0788 3.71578 12.284 3.24294 12.7569M6.9998 1H14.9998L13.9998 2V7.17157C13.9998 7.70201 14.2105 8.21071 14.5856 8.58579L19.5856 13.5858C20.8455 14.8457 19.9532 17 18.1714 17H3.82823C2.04642 17 1.15409 14.8457 2.41401 13.5858L7.41402 8.58579C7.78909 8.21071 7.9998 7.70201 7.9998 7.17157V2L6.9998 1Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `,
            search: `
        <svg class="nav-icon nav-icon--search" width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle class="nav-search-lens" cx="10.3334" cy="10" r="7.5" stroke="currentColor" stroke-width="2"/>
          <path class="nav-search-handle" d="M15.5001 15.5L20 20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path class="nav-search-glint" pathLength="1" d="M7.7 7.55C8.28 6.95 9.08 6.58 9.96 6.5" stroke="currentColor" stroke-width="1.45" stroke-linecap="round"/>
        </svg>
      `,
        };

        const handleNavItemClick = (itemId: string) => {
            activeNavItem.value = itemId;
            const selectedItem = navItems.value.find((item) => item.id === itemId);
            if (selectedItem) {
                router.push(selectedItem.route);
            }
        };

        // ----------------------------
        // 3) Шапка и подвал
        // ----------------------------
        const headerComponent = computed(() => {
            return HeaderDefault;
        });

        const footerComponent = computed(() => {
            const footerType = route.meta.footer;
            return footerType === 'Landing' ? FooterLandingComponent : FooterComponentVue;
        });
        const hideAppChrome = computed(() => Boolean(route.meta.hideAppChrome));
        // Dock auto-hides for guests; route.meta.hideAppDock only marks pages
        // where even logged-in users shouldn't see the dock (landings, auth
        // flows, full-screen meeting rooms, legal docs).
        const hideAppDock = computed(() => {
            if (route.meta.hideAppChrome || route.meta.hideAppDock) return true;
            return !store.getters.isAuthenticated;
        });

        // Form-action-bar morph (Vuex `formActionBar` module). When any page
        // commits `register` + `setDirty(true)`, the save-bar replaces the
        // dock in-place — see <transition name="action-bar-morph"> above.
        const showSaveBar = computed(() => store.getters['formActionBar/showSaveBar']);
        const saveBarState = computed(() => store.getters['formActionBar/saveBarState']);
        const onSaveClick = () => {
            // Swallow the rejection here — the page-level handler is
            // responsible for surfacing its own errors; we just need to
            // keep the bar from unhandled-rejection noise.
            store.dispatch('formActionBar/triggerSave').catch(() => undefined);
        };
        const onCancelClick = () => store.dispatch('formActionBar/triggerCancel');

        // ----------------------------
        // 4) Профильное меню
        // ----------------------------
        // ----------------------------
        // 5) Синхронизация активного пункта меню
        // ----------------------------
        const setActiveNavItem = () => {
            const matchedItem = navItems.value.find((item) => item.route === route.path);
            activeNavItem.value = matchedItem?.id;
        };

        onMounted(() => {
            setActiveNavItem();
        });

        watch(
            () => route.path,
            () => {
                setActiveNavItem();
            },
        );

        watch(
            isAuthenticated,
            (auth) => {
                if (auth) {
                    store.dispatch('fetchCalendarSummary').catch(() => undefined);
                }
            },
            { immediate: true },
        );

        return {
            isAuthenticated,
            isAdmin,
            activeNavItem,
            navItems,
            calendarBadgeCount,
            calendarHasPendingHold,
            calendarUpcomingCount,
            calendarPendingHoldCount,
            calendarHasNew,
            iconItems,
            handleNavItemClick,
            headerComponent,
            footerComponent,
            hideAppChrome,
            hideAppDock,
            showSaveBar,
            saveBarState,
            onSaveClick,
            onCancelClick,
        };
    },
};
</script>

<style>
:root {
    --app-header-height-desktop: 72px;
    --app-header-height-mobile: 72px;
    --app-floating-gap-desktop: 16px;
    --app-floating-gap-mobile: 12px;
    --app-header-max-width: 1240px;
    --app-surface-bg: rgba(255, 255, 255, 0.78);
    --app-surface-border: rgba(148, 163, 184, 0.22);
    --app-surface-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
    --app-surface-blur: 18px;
    --app-surface-radius: 18px;
    --site-footer-peek-height: 28px;
    --app-dock-page-bottom-space-desktop: 148px;
    --app-dock-page-bottom-space-mobile: 140px;
    --app-dockless-page-bottom-space-desktop: 64px;
    --app-dockless-page-bottom-space-mobile: 56px;

    /* ── Типографическая шкала ────────────────────────────────────────────
       Введена 01.09 по аудиту страницы наставника: на одном экране жили
       двадцать разных размеров, включая полушаги 11.5 / 12.5 / 13.5 / 14.5 —
       набор читался как случайный. Шаг шкалы ~1.15, якоря сохранены от
       текущего дизайна (15px основной текст, 22px заголовок карточки,
       56px имя в hero).

       Правило применения: новый и изменённый код страницы наставника берёт
       размеры отсюда; свои числа в компонентах — только с обоснованием в
       комментарии рядом. */
    --t-caption: 12px; /* микроподписи, счётчики, чипы-статусы */
    --t-small: 13px; /* вторичный текст, сноски */
    --t-ui: 14px; /* интерфейс: кнопки, лейблы, меты */
    --t-body: 15px; /* основной текст */
    --t-body-lg: 17px; /* лид, крупный абзац */
    --t-title-s: 20px; /* подзаголовок внутри блока */
    --t-title-m: 22px; /* заголовок карточки и попапа */
    --t-title-l: 26px; /* заголовок секции — крупнее карточки внутри неё */
    --t-display: 32px; /* имя наставника в hero */
    --t-display-mobile: 26px;

    /* Межстрочные интервалы под ту же шкалу. */
    --lh-tight: 1.2; /* заголовки */
    --lh-snug: 1.35; /* подзаголовки, короткие строки */
    --lh-normal: 1.55; /* основной текст */
}

@font-face {
    font-family: 'PlatformaFont';
    src: url('@/assets/fonts/TildaSans-VF_TTF_Web/TildaSans-VF.ttf') format('truetype-variations');
    /* LG-026: TildaSans — ПЕРЕМЕННЫЙ шрифт с осью wght 250…900 (проверено по
       таблице fvar). Без диапазона font-face браузер считает файл одновесным
       (400) и синтезирует всё остальное сам: 500/600/700/800 рисовались
       искусственной жирностью — «жирнее и грязнее», чем задумано, — а веса
       250–350 были недостижимы вовсе. */
    font-weight: 250 900;
    font-style: normal;
    /* Текст показывается сразу системным шрифтом и подменяется, когда файл
       догрузится, вместо невидимого текста на время загрузки. */
    font-display: swap;
}

#app {
    font-family: 'PlatformaFont', sans-serif;
    max-width: 100%;
}

.main-content {
    flex: 1;
    padding: 24px;
    padding-bottom: var(--app-dock-page-bottom-space-desktop);
    background: #f3f4f5;
    margin-left: 0;
    min-height: 100vh;
}

.main-content--fullbleed {
    padding: 0;
    background: transparent;
}

.content-wrapper {
    display: flex;
    background: #f3f4f5;
    flex-direction: column;
    padding-top: calc(var(--app-header-height-desktop) + var(--app-floating-gap-desktop));
}

.content-wrapper--fullbleed {
    padding-top: 0;
}

.nav-badge {
    position: absolute;
    top: -7px;
    right: -9px;
    min-width: 24px;
    padding: 2px 8px;
    border-radius: 999px;
    background: #4c1d95;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.nav-badge--alert {
    background: #dc2626;
}

.nav-badge--upcoming {
    background: rgba(112, 92, 226, 0.12);
    border: 1.5px solid rgba(112, 92, 226, 0.3);
    color: #705ce2;
}

.nav-badge--mobile {
    position: absolute;
    top: -6px;
    right: -8px;
}

/* «Новое в календаре» — зелёная точка слева-сверху иконки (не перекрывает
   счётчик справа). Лёгкий пульс, чтобы «было видно, что добавилось новое». */
.nav-dot--new {
    position: absolute;
    top: -3px;
    left: -3px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 0 2px #fff;
    pointer-events: none;
    animation: nav-dot-pulse 1.8s ease-in-out infinite;
}

@keyframes nav-dot-pulse {
    0%,
    100% {
        transform: scale(1);
        opacity: 1;
    }
    50% {
        transform: scale(1.25);
        opacity: 0.7;
    }
}

@media (prefers-reduced-motion: reduce) {
    .nav-dot--new {
        animation: none;
    }
}

.nav-icon-slot {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
}

.search-section {
    display: flex;
    align-items: center;
    gap: 16px;
}

.dropdown {
    position: relative;
    display: inline-block;
}

.dropdown-button {
    background: #f3f4f6;
    border: none;
    padding: 8px 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
}

.dropdown-arrow {
    font-size: 10px;
    color: #6b7280;
}

.dropdown-content {
    display: none;
    position: absolute;
    background: white;
    min-width: 160px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    z-index: 1;
}

.dropdown:hover .dropdown-content {
    display: block;
}

.dropdown-content a {
    color: #1f2937;
    padding: 12px 16px;
    text-decoration: none;
    display: block;
    font-size: 14px;
}

.dropdown-content a:hover {
    background: #f3f4f6;
}

.search-container {
    position: relative;
    display: flex;
    align-items: center;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

.search-input {
    width: 100%;
    padding: 8px 8px 8px 36px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    background: transparent;
}

.search-input:focus {
    outline: none;
}

.search-container:focus-within {
    border-color: #8162e9;
    box-shadow: 0 0 0 2px rgba(129, 98, 233, 0.1);
}

.search-button {
    position: absolute;
    left: 8px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
}

.profile {
    width: 42px;
    height: 42px;
    background: #f3f4f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    object-fit: cover;
}

.logo-section {
    cursor: pointer;
}

.profile-avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
}
.avatar-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

.fade-scale-enter-active,
.fade-scale-leave-active {
    transition:
        opacity 0.3s ease,
        transform 0.3s ease;
}

.fade-scale-enter-from {
    opacity: 0;
    transform: translateY(-50%) scale(0.95);
}
.fade-scale-enter-to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
}
.fade-scale-leave-from {
    opacity: 1;
    transform: translateY(-50%) scale(1);
}
.fade-scale-leave-to {
    opacity: 0;
    transform: translateY(-50%) scale(0.95);
}

.disable {
    display: none;
}

/* ── Action bar: morphs into the floating dock's slot when a form is
   dirty. Shares the same surface vars (--app-surface-*) so visually it
   reads as the dock changing shape rather than a second floating
   element competing for attention. */
.action-bar {
    position: fixed;
    /* safe-area: на мобильных оставляем место под home-indicator */
    bottom: calc(var(--app-floating-gap-desktop) + env(safe-area-inset-bottom, 0px));
    left: 50%;
    transform: translateX(-50%);
    /* Выше GlobalSupportFab (z-index:2000), чтобы FAB не перекрывал «Сохранить» */
    z-index: 2001;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 18px;
    backdrop-filter: blur(var(--app-surface-blur));
    -webkit-backdrop-filter: blur(var(--app-surface-blur));
    background: var(--app-surface-bg);
    border: 1px solid var(--app-surface-border);
    border-radius: var(--app-surface-radius);
    box-shadow: var(--app-surface-shadow);
    max-width: calc(100% - (var(--app-floating-gap-desktop) * 2));
    flex-wrap: wrap;
    justify-content: center;
}

@media (max-width: 1024px) {
    .action-bar {
        bottom: calc(var(--app-floating-gap-mobile) + env(safe-area-inset-bottom, 0px));
        max-width: calc(100% - (var(--app-floating-gap-mobile) * 2));
        padding: 12px 14px;
    }
}

.action-bar__cancel,
.action-bar__save {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition:
        transform 0.15s,
        box-shadow 0.15s,
        color 0.15s;
}

.action-bar__cancel {
    background: transparent;
    border: 1px solid rgba(148, 163, 184, 0.3);
    color: #475569;
}

.action-bar__cancel:hover:not(:disabled) {
    color: #5747af;
    border-color: rgba(123, 101, 247, 0.4);
}

.action-bar__save {
    background: linear-gradient(135deg, #7b65f7, #9f8dff);
    color: white;
    border: none;
    box-shadow: 0 6px 18px rgba(123, 101, 247, 0.32);
}

.action-bar__save:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(123, 101, 247, 0.4);
}

.action-bar__cancel:disabled,
.action-bar__save:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.action-bar__error {
    flex-basis: 100%;
    margin: 0;
    text-align: center;
    color: #b91c1c;
    font-size: 12px;
}

/* Morph: cross-fade with a tiny vertical slip — feels like the element
   changes shape rather than a separate widget swapping in. */
.action-bar-morph-enter-active,
.action-bar-morph-leave-active {
    transition:
        opacity 0.22s ease,
        transform 0.22s ease;
}

.action-bar-morph-enter-from,
.action-bar-morph-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(8px);
}

@media (max-width: 1024px) {
    .main-content {
        margin-left: 0;
        padding: 20px;
        padding-bottom: var(--app-dock-page-bottom-space-mobile);
    }

    .main-content.main-content--fullbleed {
        padding: 0;
    }

    .main-content.main-content--dockless:not(.main-content--fullbleed) {
        padding-bottom: var(--app-dockless-page-bottom-space-mobile);
    }

    .content-wrapper {
        padding-top: calc(var(--app-header-height-mobile) + var(--app-floating-gap-mobile));
    }

    .content-wrapper.content-wrapper--fullbleed {
        padding-top: 0;
    }

    .app-dock {
        display: flex;
        justify-content: space-around;
        align-items: center;
        flex-direction: row;
        padding: 12px 18px;
        padding-bottom: calc(env(safe-area-inset-bottom) + 12px);
        position: fixed;
        bottom: var(--app-floating-gap-mobile);
        left: 50%;
        transform: translateX(-50%);
        width: calc(100% - (var(--app-floating-gap-mobile) * 2));
        max-width: var(--app-header-max-width);
        backdrop-filter: blur(var(--app-surface-blur));
        /* Dock background uses lower opacity than the shared --app-surface-bg
       so the page underneath shows through more; the blur-filter masks the
       lower opacity. Header stays opaque (still uses --app-surface-bg). */
        background: rgba(255, 255, 255, 0.55);
        border: 1px solid var(--app-surface-border);
        border-radius: var(--app-surface-radius);
        box-shadow: var(--app-surface-shadow);
        z-index: 100;
        overflow: visible;
    }

    .app-dock .nav-item {
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 6px;
        flex: 1 1 0;
        width: auto;
        min-width: 0;
        padding: 6px;
        border-radius: 14px;
        background: transparent;
        border: 0;
        cursor: pointer;
        color: #6b7280;
        text-align: center;
        position: relative;
        transition: color 0.18s ease;
        min-height: unset;
        overflow: visible;
    }

    .app-dock .nav-item:focus-visible {
        outline: 2px solid rgba(129, 98, 233, 0.4);
        outline-offset: 4px;
    }

    .app-dock .nav-item.active {
        color: #705ce2;
    }

    .app-dock .icon-container {
        background: rgba(255, 255, 255, 0.4);
        border-radius: 16px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
        position: relative;
        transform-origin: center;
        transition:
            transform 0.18s ease,
            background 0.18s ease,
            box-shadow 0.18s ease;
        will-change: transform;
    }

    .app-dock .nav-icon-slot {
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        z-index: 1;
    }

    .app-dock .icon-container::before {
        content: '';
        position: absolute;
        inset: -5px;
        border-radius: 18px;
        background: rgba(129, 98, 233, 0.13);
        opacity: 0;
        transform: scale(0.86);
        transition:
            opacity 0.18s ease,
            transform 0.18s ease;
    }

    .app-dock .nav-item.active .icon-container {
        background: rgba(129, 98, 233, 0.2);
    }

    .app-dock .nav-item:hover .icon-container,
    .app-dock .nav-item:focus-visible .icon-container {
        background: rgba(255, 255, 255, 0.7);
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            0 8px 18px rgba(112, 92, 226, 0.16);
        transform: translateY(-3px) scale(1.12);
    }

    .app-dock .nav-item:hover .icon-container::before,
    .app-dock .nav-item:focus-visible .icon-container::before {
        opacity: 1;
        transform: scale(1);
    }

    .app-dock .icon-container svg {
        width: 18px;
        height: 18px;
        position: relative;
        z-index: 1;
        transition: transform 0.18s ease;
    }

    .app-dock .icon-container .nav-icon--home {
        width: 26px !important;
        height: 26px !important;
    }

    .app-dock .nav-item:hover .icon-container svg,
    .app-dock .nav-item:focus-visible .icon-container svg {
        transform: scale(1.08);
    }

    .nav-menu {
        display: flex;
        flex-direction: row;
        gap: 4px;
        width: 100%;
        justify-content: space-between;
    }

    .app-dock .nav-text {
        display: none;
    }
}

@media (min-width: 1025px) {
    .app-dock {
        position: fixed;
        bottom: var(--app-floating-gap-desktop);
        left: 50%;
        transform: translateX(-50%);
        width: auto;
        padding: 12px 16px;
        padding-bottom: calc(env(safe-area-inset-bottom) + 12px);
        backdrop-filter: blur(24px);
        -webkit-backdrop-filter: blur(24px);
        background: rgba(255, 255, 255, 0.9);
        border: 1px solid rgba(255, 255, 255, 0.7);
        border-radius: var(--app-surface-radius);
        box-shadow:
            0 8px 32px rgba(15, 23, 42, 0.18),
            0 2px 8px rgba(15, 23, 42, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        z-index: 100;
        display: flex;
        align-items: flex-end;
        overflow: visible;
    }

    .app-dock .nav-menu {
        display: flex;
        flex-direction: row;
        gap: 4px;
        align-items: flex-end;
        overflow: visible;
    }

    .app-dock .nav-item {
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 5px;
        min-width: 72px;
        padding: 8px 12px;
        border-radius: 14px;
        background: transparent;
        border: 0;
        cursor: pointer;
        color: #6b7280;
        text-align: center;
        position: relative;
        transition: color 0.18s ease;
        min-height: unset;
        overflow: visible;
    }

    .app-dock .nav-item:focus-visible {
        outline: 2px solid rgba(129, 98, 233, 0.4);
        outline-offset: 4px;
    }

    .app-dock .nav-item.active {
        color: #705ce2;
    }

    .app-dock .icon-container {
        background: rgba(255, 255, 255, 0.5);
        border-radius: 13px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 46px;
        height: 46px;
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            0 1px 3px rgba(15, 23, 42, 0.08);
        position: relative;
        transform-origin: center;
        transition:
            transform 0.18s ease,
            background 0.18s ease,
            box-shadow 0.18s ease;
        will-change: transform;
    }

    .app-dock .icon-container::before {
        content: '';
        position: absolute;
        inset: -6px;
        border-radius: 18px;
        background: rgba(129, 98, 233, 0.12);
        opacity: 0;
        transform: scale(0.86);
        transition:
            opacity 0.18s ease,
            transform 0.18s ease;
    }

    .app-dock .nav-item.active .icon-container {
        background: rgba(129, 98, 233, 0.2);
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.65),
            0 4px 14px rgba(112, 92, 226, 0.14);
    }

    .app-dock .icon-container svg {
        width: 20px;
        height: 20px;
        position: relative;
        z-index: 1;
        transition: transform 0.18s ease;
    }

    .app-dock .icon-container .nav-icon--home {
        width: 28px !important;
        height: 28px !important;
    }

    .app-dock .nav-text {
        font-size: 13px;
        font-weight: 600;
        color: inherit;
        white-space: nowrap;
    }

    .main-content {
        padding-bottom: var(--app-dock-page-bottom-space-desktop);
    }

    .main-content.main-content--dockless:not(.main-content--fullbleed) {
        padding-bottom: var(--app-dockless-page-bottom-space-desktop);
    }

    .app-dock .nav-item:hover .icon-container,
    .app-dock .nav-item:focus-visible .icon-container {
        transform: translateY(-5px) scale(1.14);
        background: rgba(255, 255, 255, 0.7);
        box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.85),
            0 10px 22px rgba(112, 92, 226, 0.18);
    }

    .app-dock .nav-item:hover .icon-container::before,
    .app-dock .nav-item:focus-visible .icon-container::before {
        opacity: 1;
        transform: scale(1);
    }

    .app-dock .nav-item:hover .icon-container svg,
    .app-dock .nav-item:focus-visible .icon-container svg {
        transform: scale(1.08);
    }

    .app-dock .nav-item.active:hover .icon-container {
        background: rgba(129, 98, 233, 0.25);
    }
}

.app-dock .nav-icon * {
    transform-box: fill-box;
    transform-origin: center;
}

.app-dock .nav-create-plus {
    transform-origin: 11px 11px;
}

.app-dock .nav-search-glint {
    opacity: 0;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
}

.app-dock .nav-calendar-day {
    opacity: 0.16;
    transform: scale(0.86);
}

.app-dock .nav-analytics-arrow {
    opacity: 0;
    transform: scale(0.78);
}

.app-dock .nav-home-door {
    opacity: 0.16;
    transform: scale(0.86);
    transform-origin: 12px 18px;
    transform-box: fill-box;
}

.app-dock .nav-home-roof {
    stroke-dasharray: 1;
    stroke-dashoffset: 0;
}

.app-dock .nav-home-smoke {
    opacity: 0;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
}

.app-dock .nav-home-chimney {
    transform-origin: center bottom;
    transform-box: fill-box;
}

.app-dock .nav-item--search:hover .nav-search-lens,
.app-dock .nav-item--search:focus-visible .nav-search-lens {
    animation: nav-search-lens 0.52s ease both;
}

.app-dock .nav-item--search:hover .nav-search-handle,
.app-dock .nav-item--search:focus-visible .nav-search-handle {
    animation: nav-search-handle 0.52s ease both;
}

.app-dock .nav-item--search:hover .nav-search-glint,
.app-dock .nav-item--search:focus-visible .nav-search-glint {
    animation: nav-search-glint 0.52s ease both;
}

.app-dock .nav-item--calendar:hover .nav-calendar-rings,
.app-dock .nav-item--calendar:focus-visible .nav-calendar-rings {
    animation: nav-calendar-rings 0.5s ease both;
}

.app-dock .nav-item--calendar:hover .nav-calendar-divider,
.app-dock .nav-item--calendar:focus-visible .nav-calendar-divider {
    animation: nav-calendar-divider 0.48s ease both;
}

.app-dock .nav-item--calendar:hover .nav-calendar-day,
.app-dock .nav-item--calendar:focus-visible .nav-calendar-day {
    animation: nav-calendar-day 0.48s ease both;
}

.app-dock .nav-item--analytics:hover .nav-analytics-line,
.app-dock .nav-item--analytics:focus-visible .nav-analytics-line {
    animation: nav-analytics-line 0.62s ease both;
}

.app-dock .nav-item--analytics:hover .nav-analytics-arrow,
.app-dock .nav-item--analytics:focus-visible .nav-analytics-arrow {
    animation: nav-analytics-arrow 0.62s ease both;
}

.app-dock .nav-item--create:hover .nav-create-plus,
.app-dock .nav-item--create:focus-visible .nav-create-plus {
    animation: nav-create-plus 0.55s ease both;
}

.app-dock .nav-item--create:hover .nav-create-vertical,
.app-dock .nav-item--create:focus-visible .nav-create-vertical,
.app-dock .nav-item--create:hover .nav-create-horizontal,
.app-dock .nav-item--create:focus-visible .nav-create-horizontal {
    animation: nav-create-stroke 0.55s ease both;
}

.app-dock .nav-item--home:hover .nav-home-roof,
.app-dock .nav-item--home:focus-visible .nav-home-roof {
    animation: nav-home-roof 0.55s ease both;
}

.app-dock .nav-item--home:hover .nav-home-body,
.app-dock .nav-item--home:focus-visible .nav-home-body {
    animation: nav-home-body 0.55s ease both;
}

.app-dock .nav-item--home:hover .nav-home-door,
.app-dock .nav-item--home:focus-visible .nav-home-door {
    animation: nav-home-door 0.5s ease both;
}

.app-dock .nav-item--home:hover .nav-home-smoke,
.app-dock .nav-item--home:focus-visible .nav-home-smoke {
    animation: nav-home-smoke 1.8s ease-out infinite;
}

.app-dock .nav-item--home:hover .nav-home-chimney,
.app-dock .nav-item--home:focus-visible .nav-home-chimney {
    animation: nav-home-chimney 1.8s ease-in-out infinite;
}

@keyframes nav-search-lens {
    0% {
        transform: translate(0, 0) scale(1);
    }
    45% {
        transform: translate(-1px, -1px) scale(1.1);
    }
    100% {
        transform: translate(0, 0) scale(1);
    }
}

@keyframes nav-search-handle {
    0% {
        transform: translate(0, 0);
    }
    45% {
        transform: translate(1px, 1px);
    }
    100% {
        transform: translate(0, 0);
    }
}

@keyframes nav-search-glint {
    0% {
        opacity: 0;
        stroke-dashoffset: 1;
        transform: translate(-1px, 1px);
    }
    45% {
        opacity: 0.9;
        stroke-dashoffset: 0;
        transform: translate(0, 0);
    }
    100% {
        opacity: 0.45;
        stroke-dashoffset: 0;
        transform: translate(0, 0);
    }
}

@keyframes nav-calendar-rings {
    0% {
        transform: translateY(0);
    }
    45% {
        transform: translateY(-2px);
    }
    100% {
        transform: translateY(0);
    }
}

@keyframes nav-calendar-divider {
    0% {
        transform: scaleX(0.62);
    }
    100% {
        transform: scaleX(1);
    }
}

@keyframes nav-calendar-day {
    0% {
        opacity: 0.16;
        transform: scale(0.86);
    }
    55% {
        opacity: 0.34;
        transform: scale(1.1);
    }
    100% {
        opacity: 0.26;
        transform: scale(1);
    }
}

@keyframes nav-analytics-line {
    0% {
        stroke-dasharray: 1;
        stroke-dashoffset: 1;
    }
    100% {
        stroke-dasharray: 1;
        stroke-dashoffset: 0;
    }
}

@keyframes nav-analytics-arrow {
    0%,
    45% {
        opacity: 0;
        transform: translate(-2px, 2px) scale(0.78);
    }
    100% {
        opacity: 1;
        transform: translate(0, 0) scale(1);
    }
}

@keyframes nav-create-plus {
    0% {
        transform: scale(0.96);
    }
    58% {
        transform: scale(1.1);
    }
    100% {
        transform: scale(1);
    }
}

@keyframes nav-create-stroke {
    0% {
        stroke-dasharray: 1;
        stroke-dashoffset: 0.22;
    }
    100% {
        stroke-dasharray: 1;
        stroke-dashoffset: 0;
    }
}

@keyframes nav-home-roof {
    0% {
        stroke-dasharray: 1;
        stroke-dashoffset: 1;
    }
    100% {
        stroke-dasharray: 1;
        stroke-dashoffset: 0;
    }
}

@keyframes nav-home-body {
    0% {
        transform: translateY(0);
    }
    45% {
        transform: translateY(-1.5px);
    }
    100% {
        transform: translateY(0);
    }
}

@keyframes nav-home-door {
    0% {
        opacity: 0.16;
        transform: scale(0.86);
    }
    55% {
        opacity: 0.4;
        transform: scale(1.1);
    }
    100% {
        opacity: 0.3;
        transform: scale(1);
    }
}

/* Smoke "puffs" from the chimney: line draws in from start, briefly holds at
   full visibility while drifting up, then fades — loops while user hovers. */
@keyframes nav-home-smoke {
    0% {
        opacity: 0;
        stroke-dashoffset: 1;
        transform: translateY(2px);
    }
    20% {
        opacity: 0.85;
        stroke-dashoffset: 0;
        transform: translateY(0);
    }
    65% {
        opacity: 0.85;
        transform: translateY(-1.5px);
    }
    100% {
        opacity: 0;
        stroke-dashoffset: 0;
        transform: translateY(-3px);
    }
}

/* Subtle chimney "exhale" in sync with the smoke loop. */
@keyframes nav-home-chimney {
    0%,
    100% {
        transform: scaleY(1);
    }
    50% {
        transform: scaleY(0.96);
    }
}

@media (prefers-reduced-motion: reduce) {
    .app-dock .nav-item,
    .app-dock .icon-container,
    .app-dock .icon-container::before,
    .app-dock .icon-container svg,
    .app-dock .nav-icon * {
        animation: none;
        transition: none;
    }

    .app-dock .nav-item:hover .icon-container,
    .app-dock .nav-item:focus-visible .icon-container {
        transform: none;
    }

    .app-dock .nav-item:hover .icon-container svg,
    .app-dock .nav-item:focus-visible .icon-container svg {
        transform: scale(1.08);
    }
}

/* Унификация радиуса для всех диалогов */
:global(.v-overlay__content .v-card),
:global(.v-dialog .v-card),
:global(.v-overlay--active .v-card) {
    border-radius: 14px !important;
}
</style>
